import { getTodaysDevotional } from "@/api/devotionals";
import { useResource } from "@/hooks/useResource";
import { formatLongDate, todayISO } from "@/lib/dates";
import { Link } from "@/router/Router";
import { Button } from "@/components/ui/Button";
import { Async } from "@/components/ui/Async";
import { Container, Eyebrow, Skeleton } from "@/components/ui/Primitives";
import { Reveal } from "@/components/motion/Reveal";
import { ScriptureText } from "@/components/ScriptureText";
import { Icon } from "@/components/ui/Icon";

/** Today's devotional (or the latest available, clearly dated). */
export function Devotional() {
  const today = todayISO();
  const resource = useResource(`devotional:today:${today}`, (signal) => getTodaysDevotional(today, signal));

  return (
    <section className="devo-section" aria-labelledby="devo-title">
      <Container>
        <div className="devo-section__grid">
          <div className="devo-section__intro">
            <Reveal>
              <Eyebrow index="05">Daily devotional</Eyebrow>
            </Reveal>
            <Reveal delay={60}>
              <h2 id="devo-title" className="devo-section__heading">
                The Word <em>for today</em>
              </h2>
            </Reveal>
            <Reveal delay={120}>
              <p className="devo-section__lead">Start each day with Scripture, a confession of faith and prophetic prayers from the house.</p>
            </Reveal>
            <Reveal delay={160} className="devo-section__links">
              <Link to="/devotionals" className="text-link">
                Browse the archive <Icon name="arrowRight" size={15} />
              </Link>
              <Link to="/bible" className="text-link">
                Open the Bible <Icon name="arrowRight" size={15} />
              </Link>
            </Reveal>
          </div>
          <Async
            resource={resource}
            isEmpty={(d) => !d.devotional}
            empty={{ icon: "sun", title: "Today's devotional is being prepared", text: "Check back soon — or read the Bible in the meantime.", action: <Button to="/bible" variant="ghost" size="sm">Open the Bible</Button> }}
            errorTitle="The devotional didn't load"
            skeleton={
              <div className="devo-feature" aria-hidden>
                <Skeleton className="skeleton--line skeleton--short" />
                <Skeleton className="skeleton--title" />
                <Skeleton className="skeleton--block" />
              </div>
            }
          >
            {({ devotional: d, isToday }) =>
              d ? (
                <Reveal className="devo-feature" variant="scale">
                  <p className="devo-feature__date">
                    {isToday ? <span className="devo-feature__today">Today</span> : <span className="devo-feature__today devo-feature__today--latest">Latest</span>}
                    {d.date ? formatLongDate(d.date) : null}
                  </p>
                  <h3 className="devo-feature__title">
                    <Link to={`/devotionals/${encodeURIComponent(d.id)}`}>{d.title}</Link>
                  </h3>
                  {d.keyVerseText ? (
                    <blockquote className="devo-feature__verse">
                      <p>“{d.keyVerseText}”</p>
                      {d.keyVerseReference ? (
                        <cite>
                          <ScriptureText>{d.keyVerseReference}</ScriptureText>
                        </cite>
                      ) : null}
                    </blockquote>
                  ) : d.description ? (
                    <p className="devo-feature__excerpt">{d.description}</p>
                  ) : null}
                  <div className="devo-feature__foot">
                    {d.bibleReading ? (
                      <p className="devo-feature__reading">
                        <Icon name="book" size={16} /> Bible reading: <ScriptureText>{d.bibleReading}</ScriptureText>
                      </p>
                    ) : (
                      <span />
                    )}
                    <Button to={`/devotionals/${encodeURIComponent(d.id)}`} variant="primary" iconRight="arrowRight">
                      Read today's devotional
                    </Button>
                  </div>
                </Reveal>
              ) : null
            }
          </Async>
        </div>
      </Container>
    </section>
  );
}
